import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faClipboardCheck,
  faQuestionCircle,
  faClock,
  faPlay,
  faLock,
  faTimes,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

const PracticeTestDialog = ({ 
  isOpen, 
  onClose, 
  onStart, 
  onChoosePlan, 
  testDetails, 
  isSubscribe,
  theme 
}) => {
  const isLocked = testDetails?.requires_subscription && !isSubscribe;
  const testDuration = Math.round((testDetails?.test_time || 0) / 60);

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
    },
    modal: {
      background: theme?.background?.card,
      borderRadius: '1.5rem',
      padding: '2rem',
      maxWidth: '500px',
      width: '100%',
      boxShadow: theme?.shadows?.xl,
      border: `1px solid ${theme?.borders?.primary}`,
      position: 'relative',
    },
    closeButton: {
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      width: '2rem',
      height: '2rem',
      borderRadius: '0.5rem',
      border: 'none',
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.secondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    iconWrapper: {
      width: '4rem',
      height: '4rem',
      borderRadius: '1rem',
      background: `linear-gradient(135deg, ${theme?.primary?.orange?.[400]}, ${theme?.primary?.orange?.[600]})`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '2rem',
      color: 'white',
      position: 'relative',
    },
    lockOverlay: {
      position: 'absolute',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      borderRadius: '1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      flex: 1,
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    statCard: {
      background: theme?.backgrounds?.secondary,
      padding: '1rem',
      borderRadius: '0.75rem',
      border: `1px solid ${theme?.borders?.primary}`,
      textAlign: 'center',
    },
    statIcon: {
      fontSize: '1.5rem',
      marginBottom: '0.5rem',
      color: theme?.primary?.orange?.[500],
    },
    statValue: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: theme?.text?.primary,
      marginBottom: '0.25rem',
    },
    statLabel: {
      fontSize: '0.75rem',
      color: theme?.text?.secondary,
    },
    infoBox: {
      background: theme?.backgrounds?.secondary,
      border: `1px solid ${theme?.primary?.blue?.[300]}`,
      borderRadius: '0.75rem',
      padding: '1rem',
      marginBottom: '1.5rem',
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'flex-start',
    },
    infoIcon: {
      color: theme?.primary?.blue?.[500],
      fontSize: '1rem',
      marginTop: '0.125rem',
    },
    infoText: {
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
      lineHeight: 1.6,
      flex: 1,
    },
    lockedMessage: {
      background: theme?.primary?.yellow?.[100],
      border: `1px solid ${theme?.primary?.yellow?.[300]}`,
      borderRadius: '0.75rem',
      padding: '1rem',
      marginBottom: '1.5rem',
      textAlign: 'center',
    },
    lockedText: {
      color: theme?.primary?.yellow?.[700],
      fontSize: '0.875rem',
      marginBottom: '0.5rem',
    },
    buttonContainer: {
      display: 'flex',
      gap: '0.75rem',
    },
    button: {
      flex: 1,
      padding: '0.875rem 1.5rem',
      borderRadius: '0.75rem',
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
    },
    primaryButton: {
      background: `linear-gradient(135deg, ${theme?.primary?.orange?.[500]} 0%, ${theme?.primary?.orange?.[600]} 100%)`,
      color: 'white',
      boxShadow: `0 4px 12px ${theme?.primary?.orange?.[500]}30`,
    },
    secondaryButton: {
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.primary,
      border: `1px solid ${theme?.borders?.primary}`,
    },
    premiumButton: {
      background: `linear-gradient(135deg, ${theme?.primary?.yellow?.[500]} 0%, ${theme?.primary?.yellow?.[600]} 100%)`,
      color: 'white',
      boxShadow: `0 4px 12px ${theme?.primary?.yellow?.[500]}30`,
    },
    buttonHover: {
      transform: 'translateY(-2px)',
      boxShadow: theme?.shadows?.lg,
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          style={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            style={styles.modal}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={styles.closeButton}
              onClick={onClose}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            
            <div style={styles.header}>
              <div style={styles.iconWrapper}>
                <FontAwesomeIcon icon={faClipboardCheck} />
                {isLocked && (
                  <div style={styles.lockOverlay}>
                    <FontAwesomeIcon icon={faLock} />
                  </div>
                )}
              </div>
              <h2 style={styles.title}>{testDetails?.test_name}</h2>
            </div>
            
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <FontAwesomeIcon icon={faQuestionCircle} style={styles.statIcon} />
                <div style={styles.statValue}>{testDetails?.num_questions || 0}</div>
                <div style={styles.statLabel}>Questions</div>
              </div>
              
              <div style={styles.statCard}>
                <FontAwesomeIcon icon={faClock} style={styles.statIcon} />
                <div style={styles.statValue}>{testDuration}</div>
                <div style={styles.statLabel}>Minutes</div>
              </div>
            </div>
            
            {isLocked ? (
              <div style={styles.lockedMessage}>
                <FontAwesomeIcon icon={faLock} style={{ marginBottom: '0.5rem' }} />
                <div style={styles.lockedText}>
                  This is a premium practice test
                </div>
                <div style={{ fontSize: '0.75rem', color: theme?.text?.secondary }}>
                  Upgrade to access all practice tests and track your progress
                </div>
              </div>
            ) : (
              <div style={styles.infoBox}>
                <FontAwesomeIcon icon={faInfoCircle} style={styles.infoIcon} />
                <div style={styles.infoText}>
                  This test simulates real exam conditions. You'll have {testDuration} minutes 
                  to complete {testDetails?.num_questions} questions. Your progress will be saved 
                  and you can review your answers after completion.
                </div>
              </div>
            )}
            
            <div style={styles.buttonContainer}>
              {isLocked ? (
                <>
                  <motion.button
                    style={{ ...styles.button, ...styles.secondaryButton }}
                    whileHover={styles.buttonHover}
                    whileTap={{ scale: 0.95 }}
                    onClick={onClose}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    style={{ ...styles.button, ...styles.premiumButton }}
                    whileHover={styles.buttonHover}
                    whileTap={{ scale: 0.95 }}
                    onClick={onChoosePlan}
                  >
                    <FontAwesomeIcon icon={faLock} />
                    Upgrade to Premium
                  </motion.button>
                </>
              ) : (
                <>
                  <motion.button
                    style={{ ...styles.button, ...styles.secondaryButton }}
                    whileHover={styles.buttonHover}
                    whileTap={{ scale: 0.95 }}
                    onClick={onClose}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    style={{ ...styles.button, ...styles.primaryButton }}
                    whileHover={styles.buttonHover}
                    whileTap={{ scale: 0.95 }}
                    onClick={onStart}
                  >
                    <FontAwesomeIcon icon={faPlay} />
                    Start Test
                  </motion.button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PracticeTestDialog;